import axios from 'axios'
import type { 
  FormSubmissionRequest, 
  FormSubmissionResponse, 
  FileUploadResponse,
  LegalEntityFormData,
  PhysicalPersonFormData
} from '../../../shared/types'

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3001/api'

export class ApiService {
  private axiosInstance

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 60000, // 60 seconds for file uploads
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response
      },
      (error) => {
        if (error.response?.status === 429) {
          throw new Error('Příliš mnoho požadavků. Zkuste to prosím později.')
        }
        
        if (error.response?.status === 413) {
          throw new Error('Soubor je příliš velký. Maximum je 10MB.')
        }
        
        if (error.code === 'ECONNABORTED') {
          throw new Error('Požadavek vypršel. Zkuste to prosím znovu.')
        }
        
        throw error
      }
    )
  }

  // Create session after Turnstile verification
  async createSession(turnstileToken: string): Promise<{ sessionId: string; expiresAt: string }> {
    const response = await this.axiosInstance.post('/session/create', {}, {
      headers: {
        'cf-turnstile-response': turnstileToken
      }
    })
    return response.data
  }

  // Upload a single file
  async uploadFile(file: File, fieldName: string, formType: 'legal_entity' | 'physical_person', sessionId?: string): Promise<FileUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('fieldName', fieldName)
    formData.append('formType', formType)

    const headers: any = {
      'Content-Type': 'multipart/form-data'
    }

    if (sessionId) {
      headers['x-session-id'] = sessionId
    }

    const response = await this.axiosInstance.post('/files/upload', formData, {
      headers
    })

    return response.data
  }

  // Submit form data via AJAX (JSON only)
  async submitLegalEntityForm(formData: LegalEntityFormData, sessionId?: string): Promise<FormSubmissionResponse> {
    // Extract file IDs from form data structure
    const fileIds: string[] = []
    
    console.log('🔍 [API] Collecting file IDs from legal entity form data...')
    console.log('📊 [API] Form data structure:', JSON.stringify(formData, null, 2))
    
    // Collect from step1 companyDocuments
    if (formData.step1?.companyDocuments) {
      console.log('📎 [API] Found step1.companyDocuments:', formData.step1.companyDocuments.length)
      formData.step1.companyDocuments.forEach(file => {
        console.log('📄 [API] Processing company doc:', { id: file.id, uploaded: file.uploadedToBackend })
        if (file.id && file.uploadedToBackend) {
          fileIds.push(file.id)
          console.log('✅ [API] Added company doc ID:', file.id)
        }
      })
    }
    
    // Collect from step3 identityDocuments (actual structure from payload)
    if ((formData.step3 as any)?.identityDocuments) {
      const identityDocs = (formData.step3 as any).identityDocuments;
      console.log('🆔 [API] Found step3.identityDocuments:', Array.isArray(identityDocs) ? identityDocs.length : 'not array')
      if (Array.isArray(identityDocs)) {
        identityDocs.forEach((file: any) => {
          console.log('📄 [API] Processing identity doc:', { id: file.id, uploaded: file.uploadedToBackend })
          if (file.id && file.uploadedToBackend) {
            fileIds.push(file.id)
            console.log('✅ [API] Added identity doc ID:', file.id)
          }
        })
      }
    }
    
    console.log('📋 [API] Final fileIds array:', fileIds)
    
    const submissionRequest: FormSubmissionRequest = {
      formData,
      fileIds
    }

    const headers: any = {
      'Content-Type': 'application/json'
    }

    if (sessionId) {
      headers['x-session-id'] = sessionId
    }

    const response = await this.axiosInstance.post('/forms/submit/legal-entity', submissionRequest, {
      headers
    })
    return response.data
  }

  // Submit physical person form via AJAX (JSON only)
  async submitPhysicalPersonForm(formData: PhysicalPersonFormData, sessionId?: string): Promise<FormSubmissionResponse> {
    // Form data should already contain file IDs from uploaded files
    const submissionRequest: FormSubmissionRequest = {
      formData,
      fileIds: [] // File IDs are now embedded in form data structure
    }

    const headers: any = {
      'Content-Type': 'application/json'
    }

    if (sessionId) {
      headers['x-session-id'] = sessionId
    }

    const response = await this.axiosInstance.post('/forms/submit/physical-person', submissionRequest, {
      headers
    })
    return response.data
  }

  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.axiosInstance.get('/health')
      return response.data.success
    } catch {
      return false
    }
  }
}
